import httpClient from 'api/https-client';
import { MouseEvent, useCallback } from 'react';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

/**
 * Shortcode an admin pastes into a bonus link's URL field in the admin panel.
 * When an MVP / gold member clicks that link in the rendered manual, we intercept it
 * and mint a fresh Meso Core handoff URL.
 *
 * Authoring: in the admin editor, add a normal link and set its URL to this shortcode.
 */
export const MESO_MVP_SHORTCODE = '[meso-core]';

// Match common authoring variants: `[meso-core]`, `meso-core`, `[meso-mvp]`, `meso-mvp`, `[meso]`.
// A bare `meso` is deliberately NOT matched: it would hijack ordinary training links ("mesocycle").
// The pre-rename `stride` tokens stay so manuals authored before the rename keep working.
const SHORTCODE_TOKENS = ['meso-core', 'meso-mvp', '[meso]', 'stride-core', 'stride-mvp', 'stride'];

const hrefHasMesoShortcode = (anchor: HTMLAnchorElement): boolean => {
  const raw = (anchor.getAttribute('href') || '').toLowerCase();
  const resolved = (anchor.href || '').toLowerCase();

  return SHORTCODE_TOKENS.some((token) => raw.includes(token) || resolved.includes(token));
};

export const useMesoHandoff = () => {
  const openHandoff = useCallback(async () => {
    try {
      // Mint a FRESH, short-lived (60 s, single-use) handoff URL on every click — never cache it.
      // The endpoint is gold/MVP-gated server-side (403 for everyone else → this no-ops).
      const { status, data } = await httpClient.get<{ url: string }>('/meso/handoff-url');

      if (status >= 200 && status < 300 && data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      // Graceful degrade — surface nothing scary to the member; logged for debugging.
      console.error('Failed to build Meso handoff URL', error);
    }
  }, []);

  /**
   * Delegated click handler — attach to the element that renders manual content.
   * Intercepts clicks on any link whose URL contains the Meso shortcode and
   * swaps the dead shortcode link for a freshly minted, authenticated handoff.
   */
  const handleManualClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a') as HTMLAnchorElement | null;

      if (!anchor || !hrefHasMesoShortcode(anchor)) {
        return;
      }

      event.preventDefault();
      trackAmplitudeEvent('Meso Claim Pressed');
      void openHandoff();
    },
    [openHandoff],
  );

  return { handleManualClick, openHandoff };
};
